import {
  CloudUploadIcon,
  FileRedCircleIcon,
  P,
  TrashIconRed,
} from '@vgrubs/components';
import {
  ChangeEventHandler,
  DragEvent,
  MouseEvent,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

const Wrapper = styled.div`
  width: 100%;
`;

const DropArea = styled.div<{ $dragging?: boolean; $disabled?: boolean }>`
  color: ${({ theme }) => theme.primary};
  border: 2px dashed ${({ theme }) => theme.primary};
  border-radius: 16px;
  padding: 20px;
  min-height: 130px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  text-align: center;
  transition: background 0.15s ease, border-color 0.15s ease;
  background: ${({ $dragging, theme }) =>
    $dragging ? `${theme.primary}14` : 'transparent'};

  &:hover {
    background: ${({ theme }) => `${theme.primary}0a`};
  }

  ${({ $disabled }) =>
    $disabled &&
    `
    opacity: 0.5;
    pointer-events: none;
  `}
`;

const SelectedFile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin-top: 16px;
  padding: 8px 12px;
  border-radius: 50px;
  background: ${({ theme }) => `${theme.primary}12`};
`;

const FileIconBox = styled.div`
  flex: none;
  display: flex;

  svg {
    width: 26px;
    height: 26px;
  }
`;

const TrashButton = styled.button`
  flex: none;
  display: flex;
  padding: 4px;
  background: none;
  border: none;
  cursor: pointer;

  svg {
    width: 26px;
    height: 26px;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

/**
 * Drag-and-drop file picker: a dashed area that prompts to browse / drag & drop
 * when empty, and shows the selected file (icon + name + remove) in place once
 * a file is chosen. Controlled — the parent owns `file` and updates it via
 * `onFileChange`. Clicking the area (outside the trash) reopens the picker to
 * replace the file.
 */
export const FileDropzone = ({
  file,
  onFileChange,
  accept,
  label = 'Browse files or drag & drop to upload',
  disabled,
}: {
  file: File | null;
  onFileChange: (file: File | null) => void;
  accept?: string;
  label?: string;
  disabled?: boolean;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const openPicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    onFileChange(e.target.files?.[0] ?? null);
    // reset so selecting the same file again still fires onChange
    e.target.value = '';
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) onFileChange(dropped);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!disabled) setDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
  };

  const handleClear = (e: MouseEvent) => {
    e.stopPropagation();
    onFileChange(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <Wrapper>
      <DropArea
        $dragging={dragging}
        $disabled={disabled}
        onClick={openPicker}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <HiddenInput
          ref={inputRef}
          type="file"
          accept={accept}
          disabled={disabled}
          onChange={handleChange}
        />

        <CloudUploadIcon />
        <P $m="0" $fs="16px">
          {label}
        </P>

        {file && (
          <SelectedFile>
            <FileIconBox>
              <FileRedCircleIcon />
            </FileIconBox>
            <P $m="0" $left $treeDots style={{ flex: 1, minWidth: 0 }}>
              {file.name}
            </P>
            <TrashButton
              type="button"
              onClick={handleClear}
              disabled={disabled}
              aria-label="Remove file"
            >
              <TrashIconRed />
            </TrashButton>
          </SelectedFile>
        )}
      </DropArea>
    </Wrapper>
  );
};
